import { Copy, Link2 } from "lucide-react";
import { useTransition } from "react";
import { toast } from "sonner";

import SectionCard from "@buildpy/ui/section-card";
import StatusBadge from "@buildpy/ui/status-badge";
import { Button } from "@buildpy/ui/button";
import { settleRedirect } from "@/lib/redirect-error";
import type { ReferralActionResult } from "@buildpy/core/modules/referral/action-types";
import { createReferralLink, setReferralLinkActive } from "@/api/actions/referral";

export interface ReferralLinkView {
  id: string;
  active: boolean;
  url: string;
}

interface Props {
  customerId: string;
  link: ReferralLinkView | null;
  // 이 고객의 소개로 들어온 문의(유입 REFERRAL) 수.
  referred: number;
}

const FAILED = "저장하지 못했습니다. 잠시 후 다시 시도하세요.";

// 고객 상세 「소개 링크」 — 준공 고객 한 명에 주소 하나 · 복사 · 끄기(지우기 없음).
const ReferralLinkCard = ({ customerId, link, referred }: Props) => {
  const [pending, startTransition] = useTransition();

  const run = (action: () => Promise<ReferralActionResult>, done: string) =>
    startTransition(async () => {
      try {
        const result = await action();
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success(done);
      } catch (caught) {
        if (settleRedirect(caught)) return;
        toast.error(FAILED);
      }
    });

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("소개 링크 주소를 복사했습니다");
    } catch {
      toast.error("복사하지 못했습니다");
    }
  };

  return (
    <SectionCard
      data-testid="referral-link-card"
      description="이 고객이 지인에게 보낼 주소입니다. 이 주소로 온 문의는 유입이 「소개」 로 남습니다."
      title="소개 링크">
      {!link ? (
        <Button
          data-testid="referral-link-create"
          disabled={pending}
          size="sm"
          type="button"
          variant="outline"
          onClick={() => run(() => createReferralLink({ customer_id: customerId }), "소개 링크를 만들었습니다")}>
          <Link2 className="size-4" />
          소개 링크 만들기
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge label={link.active ? "켜짐" : "꺼짐"} tone={link.active ? "done" : "dropped"} />
            <span className="text-xs text-muted-foreground" data-testid="referral-link-inquiries">
              문의 {referred}건
            </span>
          </div>
          <p className="break-all text-xs text-muted-foreground" data-testid="referral-link-url">
            {link.url}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" type="button" variant="outline" onClick={() => copy(link.url)}>
              <Copy className="size-4" />
              주소 복사
            </Button>
            <Button
              data-testid="referral-link-toggle"
              disabled={pending}
              size="sm"
              type="button"
              variant="ghost"
              onClick={() =>
                run(
                  () => setReferralLinkActive({ id: link.id, active: !link.active }),
                  link.active ? "소개 링크를 껐습니다 — 주소가 닫힙니다" : "소개 링크를 다시 켰습니다"
                )
              }>
              {link.active ? "끄기" : "다시 켜기"}
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

export default ReferralLinkCard;
